import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Title,
  SubTitle,
  Text,
  Button,
  Space,
  lightblue,
  blue,
  LeadingText,
  LeadingTextBlock,
  ResponsiveImage,
  TextBlock,
  mustardyellow,
} from "../theme/theme";
import MetaTags from "react-meta-tags";
import { useSelector } from "react-redux";
import Spinner from "../components/spinner";
import { Container, Grid } from "@mui/material";
import BlockContent from "@sanity/block-content-to-react";

import settings from "../settings.json";
import { Link, useSearchParams } from "react-router-dom";
import Landing from "./Landing";

const SUBMIT_ERRORS = {
  captcha:
    "De beveiligingscontrole is mislukt. Ververs de pagina en probeer het opnieuw.",
  invalid:
    "Niet alle velden zijn correct ingevuld. Controleer je gegevens en probeer het opnieuw.",
  server:
    "Er ging iets mis bij het verzenden. Probeer het later opnieuw, of bel ons rechtstreeks.",
};

const GENERIC_ERROR =
  "Je bericht kon niet verzonden worden. Probeer het opnieuw.";

const CONFIG_ERROR =
  "Het contactformulier is tijdelijk niet beschikbaar. Bel ons gerust rechtstreeks.";

const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY;

// TODO: implement

export const ContactContent = () => {
  const contact = useSelector((state) => state.data.contact);

  // Define styles for the form elements
  const formStyle = {
    color: blue, // Sets text color for labels
  };

  const inputStyle = {
    backgroundColor: "white", // Sets the background color of the input fields to white
    borderColor: blue, // Border color of the input fields
    borderWidth: "1px", // Border width
    borderRadius: "3px", // Rounded borders
    padding: "5px",
    paddingLeft: "10px", // Left padding inside the inputs
    width: "100%", // Makes sure the input fields are full width to align them
    boxSizing: "border-box", // Ensures paddings and borders are included in the total width and height
    marginBottom: "10px",
  };

  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");

  // Without a sitekey there is no widget, so the server would reject anything
  // sent. Say so up front rather than letting people type a message into a
  // form that cannot deliver it.
  const configMissing = !TURNSTILE_SITEKEY;
  const errorMessage = configMissing
    ? CONFIG_ERROR
    : errorCode
    ? SUBMIT_ERRORS[errorCode] || GENERIC_ERROR
    : null;

  const turnstileSlot = useRef(null);
  const widgetId = useRef(null);
  const errorBanner = useRef(null);

  // The form sits well below the fold, so the redirect back would otherwise
  // land the visitor on a page that looks unchanged.
  useEffect(() => {
    if (errorMessage && errorBanner.current) {
      errorBanner.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errorMessage, contact]);

  // Must run before the `!contact` early return below, or the hook order changes
  // between renders.
  useEffect(() => {
    if (!contact) return undefined;

    // Inlined by CRA at build time, so an unset variable is undefined here and
    // turnstile.render() would throw and take the whole page down with it.
    if (!TURNSTILE_SITEKEY) {
      console.error(
        "REACT_APP_TURNSTILE_SITEKEY was not set when this bundle was built. " +
          "The Turnstile widget cannot render, so every submission will be " +
          "rejected. Set it in the Netlify environment and redeploy."
      );
      return undefined;
    }

    let cancelled = false;
    let retry;

    const mount = () => {
      if (cancelled || widgetId.current !== null) return;
      if (!window.turnstile || !turnstileSlot.current) {
        retry = setTimeout(mount, 100);
        return;
      }
      widgetId.current = window.turnstile.render(turnstileSlot.current, {
        sitekey: TURNSTILE_SITEKEY,
      });
    };
    mount();

    return () => {
      cancelled = true;
      clearTimeout(retry);
      if (widgetId.current !== null && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [contact]);

  if (!contact) {
    return null;
  }

  return (
    <div style={{ background: lightblue }}>
      <Container>
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <Grid container spacing={2}>
          {/* Adjust the grid item sizes as necessary for your design */}
          <Grid item xs={12} sm={12} md={3} lg={3}>
            <br />
            <Title color={blue}>Contact</Title>
          </Grid>
          <Grid item xs={12} sm={12} md={9} lg={7}>
            <ResponsiveImage src={contact.header} />
            <LeadingTextBlock color={blue} description={contact.description} />

            {errorMessage && (
              <div
                ref={errorBanner}
                role="alert"
                aria-live="assertive"
                style={{
                  background: "white",
                  borderLeft: `4px solid ${mustardyellow}`,
                  borderRadius: "3px",
                  padding: "12px 16px",
                  marginBottom: "16px",
                }}
              >
                <Text color={blue}>{errorMessage}</Text>
              </div>
            )}

            <form name="contact" style={formStyle} method="POST" action="/api/contact">
              <input
                type="text"
                name="_honey"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px" }}
              />
              <p>
                <label color={blue}>
                  Naam*{" "}
                  <input type="text" name="name" required style={inputStyle} />
                </label>
              </p>
              <p>
                <label>
                  E-mail*{" "}
                  <input
                    type="email"
                    name="email"
                    required
                    style={inputStyle}
                  />
                </label>
              </p>
              <p>
                <label>
                  Bedrijf{" "}
                  <input type="text" name="company" style={inputStyle} />
                </label>
              </p>
              <p>
                <label>
                  Adres +nr{" "}
                  <input type="text" name="address" style={inputStyle} />
                </label>
              </p>
              <p>
                <label>
                  Postcode + Gemeente{" "}
                  <input type="text" name="postcode" style={inputStyle} />
                </label>
              </p>
              <p>
                <label>
                  Telefoon*{" "}
                  <input type="tel" name="phone" required style={inputStyle} />
                </label>
              </p>
              <p>
                <label>
                  Bericht*{" "}
                  <textarea
                    name="message"
                    required
                    minLength={10}
                    style={{ ...inputStyle, height: "150px" }}
                  ></textarea>
                </label>
              </p>

              <div ref={turnstileSlot} style={{ marginBottom: "10px" }} />

              <p>
                <button
                  type="submit"
                  disabled={configMissing}
                  style={{
                    ...inputStyle,
                    width: "auto",
                    cursor: configMissing ? "not-allowed" : "pointer",
                    opacity: configMissing ? 0.5 : 1,
                  }}
                >
                  <Text color={blue}>Verzenden</Text>
                </button>
              </p>
            </form>

            <TextBlock color={blue} description={contact.post} />
            <br />
            <br />
            <div
              style={{
                width: "100%",
                height: "400px",
                marginTop: "20px",
                marginLeft: "0px",
              }}
            >
              <iframe
                title="Google Maps Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2507.870959696663!2d4.417604712624051!3d51.055470443499!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c3ef9e37f04b6d%3A0xb8a48dc6c3d7166e!2sHooiendonkstraat%2097%2C%202801%20Mechelen%2C%20Belgium!5e0!3m2!1sen!2sdk!4v1711982244081!5m2!1sen!2sdk"
                frameBorder="0"
                style={{ border: 0, width: "100%", height: "100%" }}
                allowFullScreen=""
                aria-hidden="false"
                tabIndex="0"
              ></iframe>
            </div>
          </Grid>
        </Grid>
      </Container>
      <br />
      <br />
    </div>
  );
};

const Contact = () => {
  const contact = useSelector((state) => state.data.contact);

  const Meta = (
    <MetaTags>
      <title>{"MEEtec contact"}</title>
      <meta
        name="description"
        content={"Mechelse Energie Technieken - contact"}
      />
      <meta property="og:title" content={"MEEtec contact"} />
    </MetaTags>
  );

  if (!contact) {
    return (
      <>
        {Meta}
        <Spinner />
      </>
    );
  } else {
    return (
      <>
        {Meta}
        <ContactContent />
      </>
    );
  }
};

export default Contact;
